"use client";

import { FeaturedIn } from "@/components/FeaturedIn";
import {
  Box,
  Container,
  Title,
  Text,
  Button,
  SimpleGrid,
  Stack,
  Paper,
  Modal,
  Flex,
  rem,
  Divider,
} from "@mantine/core";
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import {
  IconCloudUpload,
  IconX,
  IconCamera,
  IconDownload,
} from "@tabler/icons-react";
import React, { useRef, useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { PRICING } from "@/constants/pricing";

import { WEEKLY_ANALYSES_FLOOR } from "@/lib/analysisCount";

// ============================================
// MAIN COMPONENT
// ============================================

export default function Content() {
  const router = useRouter();

  // Landing page state

  // Camera modal
  const [cameraOpen, { open: openCamera, close: closeCamera }] = useDisclosure(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const captureCanvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Screenshot zoom modal
  const [zoomImage, setZoomImage] = useState<string | null>(null);

  const isMobile = useMediaQuery("(max-width: 48em)");

  // ============================================
  // IMAGE HANDLING
  // ============================================

  const resizeImage = (file: File, maxWidth: number, maxHeight: number): Promise<string> => {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => {
        let { width, height } = img;
        const scale = Math.min(maxWidth / width, maxHeight / height, 1);
        width = Math.round(width * scale);
        height = Math.round(height * scale);

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (ctx) ctx.drawImage(img, 0, 0, width, height);

        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        URL.revokeObjectURL(img.src);
        resolve(dataUrl);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handlePhotoDrop = async (files: File[]) => {
    if (files.length > 0) {
      const dataUrl = await resizeImage(files[0], 1200, 1200);
      sessionStorage.setItem("colorAnalysisImage", dataUrl);
      router.push("/ai-color-analysis/result");
    }
  };

  // Camera functions
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      }
    } catch (err) {
      console.error("Camera error:", err);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const takePhoto = async () => {
    if (videoRef.current && captureCanvasRef.current) {
      const video = videoRef.current;
      const canvas = captureCanvasRef.current;

      let width = video.videoWidth;
      let height = video.videoHeight;
      const scale = Math.min(1200 / width, 1200 / height, 1);
      width = Math.round(width * scale);
      height = Math.round(height * scale);

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(video, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        sessionStorage.setItem("colorAnalysisImage", dataUrl);
        closeCamera();
        stopCamera();
        router.push("/ai-color-analysis/result");
      }
    }
  };

  const handleOpenCamera = () => {
    openCamera();
    setTimeout(() => startCamera(), 100);
  };

  const handleCloseCamera = () => {
    stopCamera();
    closeCamera();
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <Box mt={{ base: "1rem", md: "3.5rem" }} pos="relative">
      {/* Hero Section */}
      <Box pt={{ base: 0, md: "md" }} pb={{ base: "sm", md: "xl" }}>
        <Container size="lg">
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={{ base: "md", md: "xl" }}>
            {/* Left: Copy */}
            <Stack gap="sm" justify="center">
              <Title
                order={1}
                fz={{ base: "1.5rem", md: "2.5rem" }}
                fw={700}
                style={{ lineHeight: 1.15, color: "#1E293B" }}
                ta={{ base: "center", md: "left" }}
              >
                Free Color Analysis for Men
              </Title>
              <Text fz={{ base: "sm", md: "lg" }} ta={{ base: "center", md: "left" }} style={{ color: "#64748B" }}>
                Upload a photo and discover which suit colors, shirt shades, and casual wear make you look your best — in under 2 minutes.
              </Text>
              <Text fz="xs" ta={{ base: "center", md: "left" }} style={{ color: "#94A3B8" }}>
                No signup · ~2 min · Photos stay private
              </Text>
            </Stack>

            {/* Right: Upload Box */}
            <Paper
              radius="lg"
              p={{ base: "md", md: "xl" }}
              style={{
                background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
                border: "1px solid #E2E8F0",
                boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
              }}
            >
              <Stack gap="sm">
                <Box ta="center">
                  <Text fw={600} fz={{ base: "md", md: "lg" }} style={{ color: "#1E293B" }}>
                    Upload Photo → Get Your Colors
                  </Text>
                </Box>

                <Dropzone
                  onDrop={handlePhotoDrop}
                  maxSize={10 * 1024 ** 2}
                  accept={IMAGE_MIME_TYPE}
                  multiple={false}
                  h={130}
                  radius="lg"
                  styles={{
                    root: {
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "white",
                      border: "2px dashed #CBD5E1",
                      "&:hover": {
                        borderColor: "#3B82F6",
                        backgroundColor: "#F8FAFC",
                      },
                    },
                  }}
                >
                  <Flex direction="column" align="center" justify="center" style={{ pointerEvents: "none" }}>
                    <Dropzone.Accept>
                      <IconDownload style={{ width: rem(36), height: rem(36), color: "#22C55E" }} stroke={1.5} />
                    </Dropzone.Accept>
                    <Dropzone.Reject>
                      <IconX style={{ width: rem(36), height: rem(36), color: "#EF4444" }} stroke={1.5} />
                    </Dropzone.Reject>
                    <Dropzone.Idle>
                      <IconCloudUpload style={{ width: rem(36), height: rem(36), color: "#94A3B8" }} stroke={1.5} />
                    </Dropzone.Idle>
                    <Text size="sm" ta="center" mt="xs" fw={500} style={{ color: "#475569" }}>
                      Drag photo here or click to select
                    </Text>
                    <Text size="xs" ta="center" mt={4} style={{ color: "#94A3B8" }}>
                      Best: shoulders-up, clear view of face
                    </Text>
                  </Flex>
                </Dropzone>

                {!isMobile && (
                  <>
                    <Divider label="or" labelPosition="center" color="gray.3" my={0} />

                    <Button
                      variant="light"
                      color="blue"
                      size="md"
                      radius="md"
                      leftSection={<IconCamera size={18} />}
                      onClick={handleOpenCamera}
                      fullWidth
                    >
                      Use Your Camera
                    </Button>
                  </>
                )}
              </Stack>
            </Paper>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Social Proof Strip */}
      <Box py={{ base: "sm", md: "lg" }} bg="#F8FAFC">
        <Container size="md" ta="center">
          <Title order={2} fz={{ base: "1rem", md: "1.75rem" }} mb={4} style={{ color: "#1E293B" }}>
            <Text span fz={{ base: "1.25rem", md: "2rem" }} fw={700} style={{ color: "#3B82F6" }}>
              {WEEKLY_ANALYSES_FLOOR}+
            </Text>{" "}
            color analyses run every week
          </Title>
          <Box mt={{ base: "xs", md: "sm" }} mb={{ base: "sm", md: "lg" }}>
            <FeaturedIn />
          </Box>

          {/* Preview Strip */}
          <SimpleGrid cols={3} spacing={{ base: 10, md: 16 }} maw={{ base: 340, md: 700 }} mx="auto">
            {[
              "/males/casual1.png",
              "/males/professional1.png",
              "/males/casual2.png",
            ].map((src, i) => (
              <Box
                key={i}
                style={{
                  borderRadius: 12,
                  overflow: "hidden",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                  aspectRatio: "1",
                }}
              >
                <img
                  src={src}
                  alt={`Men's color analysis preview ${i + 1}`}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  loading="lazy"
                />
              </Box>
            ))}
          </SimpleGrid>
          <Text fz="xs" mt="xs" style={{ color: "#94A3B8" }}>
            Real AI-generated outfit previews
          </Text>
        </Container>
      </Box>

      {/* See Real Season Results */}
      <Box py={{ base: "sm", md: "lg" }}>
        <Container size="lg">
          <Title order={2} ta="center" mb={{ base: "xs", md: "md" }} fz={{ base: "1.1rem", md: "1.75rem" }} style={{ color: "#1E293B" }}>
            See Real Color Analysis Results
          </Title>
          {/* Mobile: horizontal scroll */}
          <Box
            hiddenFrom="sm"
            style={{
              display: "flex",
              gap: 12,
              overflowX: "auto",
              paddingBottom: 8,
              WebkitOverflowScrolling: "touch",
              scrollSnapType: "x mandatory",
            }}
          >
            {[
              "/swatchPreviews/season1.png",
              "/swatchPreviews/season2.png",
              "/swatchPreviews/season3.png",
            ].map((src, i) => (
              <Box
                key={i}
                onClick={() => setZoomImage(src)}
                style={{
                  flexShrink: 0,
                  width: 260,
                  background: "white",
                  borderRadius: 20,
                  padding: 8,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
                  scrollSnapAlign: "start",
                  cursor: "pointer",
                }}
              >
                <Box style={{ borderRadius: 12, overflow: "hidden" }}>
                  <img
                    src={src}
                    alt={`Men's seasonal color analysis result ${i + 1}`}
                    style={{ width: "100%", height: "auto", display: "block" }}
                    loading="lazy"
                  />
                </Box>
              </Box>
            ))}
          </Box>
          {/* Desktop: centered grid */}
          <SimpleGrid cols={3} spacing="lg" visibleFrom="sm">
            {[
              "/swatchPreviews/season1.png",
              "/swatchPreviews/season2.png",
              "/swatchPreviews/season3.png",
            ].map((src, i) => (
              <Box
                key={i}
                onClick={() => setZoomImage(src)}
                style={{
                  background: "white",
                  borderRadius: 20,
                  padding: 12,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
                  cursor: "pointer",
                  transition: "transform 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.02)")}
                onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
              >
                <Box style={{ borderRadius: 12, overflow: "hidden" }}>
                  <img
                    src={src}
                    alt={`Men's seasonal color analysis result ${i + 1}`}
                    style={{ width: "100%", height: "auto", display: "block" }}
                    loading="lazy"
                  />
                </Box>
              </Box>
            ))}
          </SimpleGrid>
          <Text fz="xs" ta="center" mt="xs" style={{ color: "#94A3B8" }}>
            Tap to view full result
          </Text>
        </Container>
      </Box>

      {/* 60+ Personalized Colors */}
      <Box py={{ base: "sm", md: "lg" }} bg="#F8FAFC">
        <Container size="lg">
          <Title order={2} ta="center" mb={{ base: "xs", md: "md" }} fz={{ base: "1.1rem", md: "1.75rem" }} style={{ color: "#1E293B" }}>
            60+ Personalized Colors With Hex Codes
          </Title>
          {/* Mobile: horizontal scroll */}
          <Box
            hiddenFrom="sm"
            style={{
              display: "flex",
              gap: 12,
              overflowX: "auto",
              paddingBottom: 8,
              WebkitOverflowScrolling: "touch",
              scrollSnapType: "x mandatory",
            }}
          >
            {[
              "/swatchPreviews/palette1.png",
              "/swatchPreviews/palette2.png",
            ].map((src, i) => (
              <Box
                key={i}
                onClick={() => setZoomImage(src)}
                style={{
                  flexShrink: 0,
                  width: "85%",
                  background: "white",
                  borderRadius: 20,
                  padding: 8,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
                  scrollSnapAlign: "center",
                  cursor: "pointer",
                }}
              >
                <Box style={{ borderRadius: 12, overflow: "hidden" }}>
                  <img
                    src={src}
                    alt={`Men's color palette ${i + 1}`}
                    style={{ width: "100%", height: "auto", display: "block" }}
                    loading="lazy"
                  />
                </Box>
              </Box>
            ))}
          </Box>
          {/* Desktop: side by side */}
          <SimpleGrid cols={2} spacing="lg" visibleFrom="sm">
            {[
              "/swatchPreviews/palette1.png",
              "/swatchPreviews/palette2.png",
            ].map((src, i) => (
              <Box
                key={i}
                onClick={() => setZoomImage(src)}
                style={{
                  background: "white",
                  borderRadius: 20,
                  padding: 12,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
                  cursor: "pointer",
                }}
              >
                <Box style={{ borderRadius: 12, overflow: "hidden" }}>
                  <img
                    src={src}
                    alt={`Men's color palette ${i + 1}`}
                    style={{ width: "100%", height: "auto", display: "block" }}
                    loading="lazy"
                  />
                </Box>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Outfit Categories for Men */}
      <Box py={{ base: "sm", md: "lg" }}>
        <Container size="lg">
          <Title order={2} ta="center" mb={{ base: "xs", md: "md" }} fz={{ base: "1.1rem", md: "1.75rem" }} style={{ color: "#1E293B" }}>
            AI-Generated Outfit Previews for Men
          </Title>
          {/* Mobile: horizontal scroll */}
          <Box
            hiddenFrom="md"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              overflowX: "auto",
              paddingBottom: 8,
              WebkitOverflowScrolling: "touch",
              scrollSnapType: "x mandatory",
            }}
          >
            {[
              { src: "/males/casual1.png", label: "Casual & Everyday" },
              { src: "/males/professional1.png", label: "Professional & Suits" },
              { src: "/males/formal1.png", label: "Formal & Evening" },
            ].map((item, i) => (
              <Box
                key={i}
                onClick={() => setZoomImage(item.src)}
                style={{
                  flexShrink: 0,
                  width: "75%",
                  background: "white",
                  borderRadius: 20,
                  padding: 8,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
                  scrollSnapAlign: "center",
                  cursor: "pointer",
                }}
              >
                <Text fw={600} mb={4} fz="sm" ta="center" style={{ color: "#1E293B" }}>
                  {item.label}
                </Text>
                <Box style={{ borderRadius: 12, overflow: "hidden" }}>
                  <img
                    src={item.src}
                    alt={`Men's ${item.label} color recommendations`}
                    style={{ width: "100%", height: "auto", display: "block" }}
                    loading="lazy"
                  />
                </Box>
              </Box>
            ))}
          </Box>
          {/* Desktop: 3 columns */}
          <SimpleGrid cols={3} spacing="lg" visibleFrom="md">
            {[
              { src: "/males/casual1.png", label: "Casual & Everyday" },
              { src: "/males/professional1.png", label: "Professional & Suits" },
              { src: "/males/formal1.png", label: "Formal & Evening" },
            ].map((item, i) => (
              <Box key={i} ta="center">
                <Text fw={600} mb={4} fz="md" style={{ color: "#1E293B" }}>
                  {item.label}
                </Text>
                <Box
                  onClick={() => setZoomImage(item.src)}
                  style={{
                    background: "white",
                    borderRadius: 20,
                    padding: 10,
                    boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
                    cursor: "pointer",
                  }}
                >
                  <Box style={{ borderRadius: 12, overflow: "hidden" }}>
                    <img
                      src={item.src}
                      alt={`Men's ${item.label} color recommendations`}
                      style={{ width: "100%", height: "auto", display: "block" }}
                      loading="lazy"
                    />
                  </Box>
                </Box>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Metals & Neutrals Section */}
      <Box py={{ base: "sm", md: "lg" }} bg="#F8FAFC">
        <Container size="lg">
          <Title order={2} ta="center" mb={{ base: "xs", md: "md" }} fz={{ base: "1.1rem", md: "1.75rem" }} style={{ color: "#1E293B" }}>
            Metal & Neutral Recommendations
          </Title>
          <Text ta="center" fz="sm" mb="md" style={{ color: "#64748B" }} maw={600} mx="auto">
            Your color analysis includes which metals (gold, silver, rose gold) suit your undertone, plus your personalized neutral shades for versatile wardrobe basics.
          </Text>
          {/* Mobile: horizontal swipe carousel */}
          <Box
            hiddenFrom="md"
            style={{
              display: "flex",
              gap: 12,
              overflowX: "auto",
              paddingBottom: 8,
              WebkitOverflowScrolling: "touch",
              scrollSnapType: "x mandatory",
            }}
          >
            {[
              { src: "/males/maleNeutrals.png", alt: "Men's neutral color recommendations" },
              { src: "/males/maleMetals.png", alt: "Metal recommendations for men" },
            ].map((item, i) => (
              <Box
                key={i}
                onClick={() => setZoomImage(item.src)}
                style={{
                  flexShrink: 0,
                  width: "85%",
                  background: "white",
                  borderRadius: 20,
                  padding: 8,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
                  scrollSnapAlign: "center",
                  cursor: "pointer",
                }}
              >
                <Box style={{ borderRadius: 12, overflow: "hidden" }}>
                  <img
                    src={item.src}
                    alt={item.alt}
                    style={{ width: "100%", height: "auto", display: "block" }}
                    loading="lazy"
                  />
                </Box>
              </Box>
            ))}
          </Box>
          {/* Desktop: side by side */}
          <SimpleGrid cols={2} spacing="lg" visibleFrom="md">
            {[
              { src: "/males/maleNeutrals.png", alt: "Men's neutral color recommendations" },
              { src: "/males/maleMetals.png", alt: "Metal recommendations for men" },
            ].map((item, i) => (
              <Box
                key={i}
                onClick={() => setZoomImage(item.src)}
                style={{
                  background: "white",
                  borderRadius: 20,
                  padding: 12,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
                  cursor: "pointer",
                }}
              >
                <Box style={{ borderRadius: 12, overflow: "hidden" }}>
                  <img
                    src={item.src}
                    alt={item.alt}
                    style={{ width: "100%", height: "auto", display: "block" }}
                    loading="lazy"
                  />
                </Box>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Why Color Analysis Matters for Men - Educational Content */}
      <Box py={{ base: "md", md: "xl" }}>
        <Container size="lg">
          <Title order={2} ta="center" mb={{ base: "sm", md: "md" }} fz={{ base: "1.25rem", md: "2rem" }} style={{ color: "#1E293B" }}>
            Why Color Analysis Matters for Men
          </Title>
          <Text ta="center" fz={{ base: "sm", md: "md" }} mb="xl" style={{ color: "#64748B" }} maw={700} mx="auto">
            Most men own clothes in colors that don&apos;t complement their natural coloring. Color analysis for men identifies your best shades so every purchase works harder for you.
          </Text>

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg" mb="xl">
            <Paper p="lg" radius="md" style={{ border: "1px solid #E2E8F0" }}>
              <Title order={3} fz="lg" mb="sm" style={{ color: "#1E293B" }}>
                Professional Impact
              </Title>
              <Text fz="sm" style={{ color: "#64748B" }}>
                The right suit and shirt colors make you look more authoritative and trustworthy. Studies show people form impressions within seconds — wearing colors that complement your skin tone makes you appear healthier, more confident, and more competent in interviews, meetings, and presentations.
              </Text>
            </Paper>

            <Paper p="lg" radius="md" style={{ border: "1px solid #E2E8F0" }}>
              <Title order={3} fz="lg" mb="sm" style={{ color: "#1E293B" }}>
                Simplified Shopping
              </Title>
              <Text fz="sm" style={{ color: "#64748B" }}>
                Stop buying clothes that &quot;looked good in the store&quot; but never get worn. With your color palette, you&apos;ll know exactly which shades of navy, gray, brown, and accent colors work for you before you buy. No more guessing, no more wasted money.
              </Text>
            </Paper>

            <Paper p="lg" radius="md" style={{ border: "1px solid #E2E8F0" }}>
              <Title order={3} fz="lg" mb="sm" style={{ color: "#1E293B" }}>
                Cohesive Wardrobe
              </Title>
              <Text fz="sm" style={{ color: "#64748B" }}>
                When everything in your closet is in your color palette, mixing and matching becomes effortless. Your shirts work with multiple suits, your ties complement your jackets, and getting dressed takes minutes instead of a frustrating process of elimination.
              </Text>
            </Paper>
          </SimpleGrid>
        </Container>
      </Box>

      {/* What You'll Learn Section */}
      <Box py={{ base: "md", md: "xl" }} bg="#F8FAFC">
        <Container size="lg">
          <Title order={2} ta="center" mb={{ base: "sm", md: "md" }} fz={{ base: "1.25rem", md: "2rem" }} style={{ color: "#1E293B" }}>
            What Your Color Analysis Includes
          </Title>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Box>
              <Title order={3} fz="lg" mb="sm" style={{ color: "#1E293B" }}>
                Your Season & Sub-Season
              </Title>
              <Text fz="sm" mb="md" style={{ color: "#64748B" }}>
                You&apos;ll discover whether you&apos;re a Spring, Summer, Autumn, or Winter — plus your specific sub-season (like Deep Autumn or Cool Winter). This tells you whether you look best in warm or cool colors, muted or bright shades, and light or dark tones.
              </Text>

              <Title order={3} fz="lg" mb="sm" style={{ color: "#1E293B" }}>
                Suit & Shirt Combinations
              </Title>
              <Text fz="sm" mb="md" style={{ color: "#64748B" }}>
                Get specific recommendations for suit colors (not just &quot;navy&quot; but which shade of navy), shirt colors that complement your skin, and how to combine them. Warm-toned men might excel in chocolate brown and olive; cool-toned men in charcoal and burgundy.
              </Text>
            </Box>

            <Box>
              <Title order={3} fz="lg" mb="sm" style={{ color: "#1E293B" }}>
                60+ Color Palette with Hex Codes
              </Title>
              <Text fz="sm" mb="md" style={{ color: "#64748B" }}>
                Your personalized palette includes over 60 colors organized by category: neutrals, accent colors, and colors to avoid. Each comes with a hex code so you can match online shopping to your exact shades.
              </Text>

              <Title order={3} fz="lg" mb="sm" style={{ color: "#1E293B" }}>
                Casual & Accessory Colors
              </Title>
              <Text fz="sm" mb="md" style={{ color: "#64748B" }}>
                Beyond suits, you&apos;ll learn which t-shirt colors, sweater shades, and denim washes work for you. Plus recommendations for ties, pocket squares, watch straps, and even sunglasses frames that complement your coloring.
              </Text>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Men's Color Season Examples */}
      <Box py={{ base: "md", md: "xl" }}>
        <Container size="lg">
          <Title order={2} ta="center" mb={{ base: "sm", md: "md" }} fz={{ base: "1.25rem", md: "2rem" }} style={{ color: "#1E293B" }}>
            Color Seasons for Men: Examples
          </Title>
          <Text ta="center" fz={{ base: "sm", md: "md" }} mb="lg" style={{ color: "#64748B" }} maw={700} mx="auto">
            Each season has distinct characteristics. Here&apos;s what the four main seasons typically look like for men:
          </Text>

          <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
            <Paper p="md" radius="md" style={{ border: "1px solid #E2E8F0" }}>
              <Box mb="sm" style={{ borderRadius: 8, overflow: "hidden" }}>
                <img
                  src="/males/lightspring.webp"
                  alt="Light Spring man example"
                  style={{ width: "100%", height: "auto", display: "block" }}
                  loading="lazy"
                />
              </Box>
              <Title order={4} fz="md" mb="xs" style={{ color: "#2D5A27" }}>Spring Men</Title>
              <Text fz="xs" mb="sm" style={{ color: "#64748B" }}>
                Warm undertones, often with golden or peachy skin. Light to medium hair with warm tones. Clear, bright eyes.
              </Text>
              <Text fz="xs" fw={600} style={{ color: "#1E293B" }}>Best colors:</Text>
              <Text fz="xs" style={{ color: "#64748B" }}>Camel, coral, warm navy, ivory, peach, clear teal</Text>
            </Paper>

            <Paper p="md" radius="md" style={{ border: "1px solid #E2E8F0" }}>
              <Box mb="sm" style={{ borderRadius: 8, overflow: "hidden" }}>
                <img
                  src="/males/coolsummer.webp"
                  alt="Cool Summer man example"
                  style={{ width: "100%", height: "auto", display: "block" }}
                  loading="lazy"
                />
              </Box>
              <Title order={4} fz="md" mb="xs" style={{ color: "#4A6B8A" }}>Summer Men</Title>
              <Text fz="xs" mb="sm" style={{ color: "#64748B" }}>
                Cool undertones with pink or rosy skin. Ash-toned hair (blonde to brown). Soft, muted eye colors.
              </Text>
              <Text fz="xs" fw={600} style={{ color: "#1E293B" }}>Best colors:</Text>
              <Text fz="xs" style={{ color: "#64748B" }}>Soft navy, rose, powder blue, lavender, slate gray, mauve</Text>
            </Paper>

            <Paper p="md" radius="md" style={{ border: "1px solid #E2E8F0" }}>
              <Box mb="sm" style={{ borderRadius: 8, overflow: "hidden" }}>
                <img
                  src="/males/softautumn.webp"
                  alt="Soft Autumn man example"
                  style={{ width: "100%", height: "auto", display: "block" }}
                  loading="lazy"
                />
              </Box>
              <Title order={4} fz="md" mb="xs" style={{ color: "#8B5A2B" }}>Autumn Men</Title>
              <Text fz="xs" mb="sm" style={{ color: "#64748B" }}>
                Warm undertones with golden or olive skin. Rich hair colors (auburn, chestnut, warm brown). Warm eye colors.
              </Text>
              <Text fz="xs" fw={600} style={{ color: "#1E293B" }}>Best colors:</Text>
              <Text fz="xs" style={{ color: "#64748B" }}>Olive, rust, chocolate brown, terracotta, forest green, mustard</Text>
            </Paper>

            <Paper p="md" radius="md" style={{ border: "1px solid #E2E8F0" }}>
              <Box mb="sm" style={{ borderRadius: 8, overflow: "hidden" }}>
                <img
                  src="/males/deepwinter.webp"
                  alt="Deep Winter man example"
                  style={{ width: "100%", height: "auto", display: "block" }}
                  loading="lazy"
                />
              </Box>
              <Title order={4} fz="md" mb="xs" style={{ color: "#1E3A5F" }}>Winter Men</Title>
              <Text fz="xs" mb="sm" style={{ color: "#64748B" }}>
                Cool undertones, often with high contrast. Dark hair, light to dark skin. Clear, striking eye colors.
              </Text>
              <Text fz="xs" fw={600} style={{ color: "#1E293B" }}>Best colors:</Text>
              <Text fz="xs" style={{ color: "#64748B" }}>True black, pure white, royal blue, burgundy, emerald, true red</Text>
            </Paper>
          </SimpleGrid>
        </Container>
      </Box>

      {/* FAQ Section - Men-focused */}
      <Box py={{ base: "sm", md: "lg" }}>
        <Container size="xl">
          <Title order={2} ta="center" mb={{ base: "sm", md: "lg" }} style={{ color: "#1E293B" }}>
            Color Analysis for Men: FAQ
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            {[
              {
                question: "What is color analysis for men?",
                answer: "Color analysis for men determines which colors look best on you based on your skin tone, hair color, and eye color. Our AI identifies your seasonal color palette (Spring, Summer, Autumn, or Winter) and provides specific recommendations for suits, shirts, ties, and casual wear.",
              },
              {
                question: "How does it work?",
                answer: "Upload a clear photo of your face. Our AI evaluates your undertones, contrast levels, and natural coloring to determine your season. You'll get a 60+ color palette with hex codes, plus outfit recommendations tailored for men.",
              },
              {
                question: "Is color analysis accurate for men?",
                answer: "Yes — our system has been trained on thousands of professional consultations for both men and women. Many users find results comparable to in-person sessions that typically cost $200-400.",
              },
              {
                question: "How much does color analysis for men cost?",
                answer: `The preview is free and shows your top 2 likely seasons. The full analysis is $${PRICING.AI_COLOR_ANALYSIS.BASE_PRICE} and includes your exact season, personalized palette, and outfit recommendations for professional and casual wear.`,
              },
              {
                question: "What photo should I use?",
                answer: "A clear, well-lit selfie facing the camera works best. Natural lighting is ideal. Avoid sunglasses or heavy shadows. If you have facial hair, that's fine — the AI focuses on your skin tone and eye color.",
              },
              {
                question: "What's included in the full analysis?",
                answer: "Your exact season and sub-season, a 60+ color palette with hex codes, and 14 digital outfit renders showing you in your best suit colors, casual wear, and accessories.",
              },
            ].map((faq, index) => (
              <Paper key={index} p="lg" radius="lg" bg="white" style={{ border: "1px solid #E2E8F0" }}>
                <Title order={4} mb="sm" fz="md" style={{ color: "#1E293B" }}>
                  {faq.question}
                </Title>
                <Text fz="sm" style={{ color: "#64748B" }}>
                  {faq.answer}
                </Text>
              </Paper>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Camera Modal */}
      <Modal opened={cameraOpen} onClose={handleCloseCamera} title="Take a Selfie" centered size="md">
        <Stack>
          <Box h={350} style={{ overflow: "hidden", borderRadius: 12 }}>
            <video ref={videoRef} autoPlay playsInline style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </Box>
          <Button onClick={takePhoto} leftSection={<IconCamera size={18} />} color="blue" size="lg" radius="md">
            Capture Photo
          </Button>
          <canvas ref={captureCanvasRef} style={{ display: "none" }} />
        </Stack>
      </Modal>

      {/* Screenshot Zoom Modal */}
      <Modal
        opened={!!zoomImage}
        onClose={() => setZoomImage(null)}
        size="xl"
        centered
        padding={0}
        withCloseButton={false}
        styles={{
          body: { padding: 0 },
          content: { background: "transparent", boxShadow: "none" },
        }}
        overlayProps={{ backgroundOpacity: 0.7 }}
      >
        <Box
          onClick={() => setZoomImage(null)}
          style={{
            cursor: "pointer",
            maxHeight: "90vh",
            overflow: "auto",
            borderRadius: 16,
            background: "white",
          }}
        >
          {zoomImage && (
            <img
              src={zoomImage}
              alt="Full preview"
              style={{ width: "100%", height: "auto", display: "block", borderRadius: 16 }}
            />
          )}
        </Box>
      </Modal>
    </Box>
  );
}
